/**
 * Firefly Effect — Canvas Version (Pro)
 * Optimized for high performance and smooth animation across all browsers.
 */

import './style.scss';

/**
 * Firefly particle class
 */
class Firefly {
	constructor(stage, config) {
		this.stage = stage;
		this.config = config;
		this.reset();
	}

	/**
	 * Initialize or reset firefly properties.
	 * Calculated once to avoid expensive math in the high-frequency animation loop.
	 */
	reset() {
		// Random position within canvas
		this.x = Math.random() * this.stage.width;
		this.y = Math.random() * this.stage.height;

		// Base size from config
		this.size =
			this.config.minPixel +
			Math.random() * (this.config.maxPixel - this.config.minPixel);

		// Random color from provided palette
		this.color =
			this.config.palettes[
				Math.floor(Math.random() * this.config.palettes.length)
			];

		// Random movement angle and velocity
		this.ang = Math.random() * 2 * Math.PI;
		this.v = this.config.speed * (0.5 + Math.random() * 0.5);

		// Random initial opacity and blinking cycle
		this.opacity = Math.random();
		this.blinkSpeed = 0.005 + Math.random() * 0.01;

		// Pre-calculate render size (including glow padding) to save CPU cycles in draw loop
		// A glow factor of 9 roughly translates to a radius multiplier of 3x.
		this.renderSize = this.size * (this.config.glow * 0.5 + 2);
	}

	/**
	 * Update firefly position and state based on time delta
	 * @param {Object} mouse Current mouse coordinates
	 * @param {number} dt Delta time multiplier (normalized to 1.0 at 60fps)
	 */
	update(mouse, dt = 1) {
		// Organic jitter movement
		this.x += this.v * Math.cos(this.ang) * dt;
		this.y += this.v * Math.sin(this.ang) * dt;
		this.ang += (Math.random() * 0.2 - 0.1) * dt;

		// Interactive mouse repulsion
		if (this.config.interactive && mouse.x !== null) {
			const dx = this.x - mouse.x;
			const dy = this.y - mouse.y;
			const distSq = dx * dx + dy * dy; // Use squared distance for performance
			const radius = 150;

			if (distSq < radius * radius) {
				const dist = Math.sqrt(distSq);
				const force = (radius - dist) / radius;
				this.x += (dx / dist) * force * 8 * dt;
				this.y += (dy / dist) * force * 8 * dt;
			}
		}

		// Update blinking opacity
		this.opacity += this.blinkSpeed * dt;
		if (this.opacity >= 1) {
			this.opacity = 1;
			this.blinkSpeed = -Math.abs(this.blinkSpeed);
		} else if (this.opacity <= 0) {
			this.opacity = 0;
			this.blinkSpeed = Math.abs(this.blinkSpeed);
		}

		// Boundary check: wrap firefly around the stage with padding
		const padding = 100;
		if (this.x < -padding) this.x = this.stage.width + padding;
		else if (this.x > this.stage.width + padding) this.x = -padding;

		if (this.y < -padding) this.y = this.stage.height + padding;
		else if (this.y > this.stage.height + padding) this.y = -padding;
	}

	/**
	 * Draw the firefly sprite on the main canvas
	 * @param {CanvasRenderingContext2D} ctx Destination context
	 * @param {Object} sprites Collection of pre-rendered sprite canvases
	 */
	draw(ctx, sprites) {
		const sprite = sprites[this.color];
		if (!sprite) return;

		// Apply opacity and draw the pre-rendered texture
		ctx.globalAlpha = Math.max(0, this.opacity * this.config.opacity);
		ctx.drawImage(
			sprite,
			this.x - this.renderSize / 2,
			this.y - this.renderSize / 2,
			this.renderSize,
			this.renderSize
		);
	}
}

/**
 * Initialize firefly engine for a given container
 * @param {HTMLElement} stage The container element with data-attributes
 */
const initFireflies = (stage) => {
	if (stage.dataset.initialized) return;
	stage.dataset.initialized = 'true';

	// Setup drawing canvas
	const canvas = document.createElement('canvas');
	Object.assign(canvas.style, {
		position: 'absolute',
		top: '0',
		left: '0',
		width: '100%',
		height: '100%',
		pointerEvents: 'none',
	});
	stage.appendChild(canvas);

	const ctx = canvas.getContext('2d', { alpha: true });
	const computedStyle = getComputedStyle(stage);

	// Parse settings from data-attributes and CSS variables
	let palettes = ['#ffffff'];
	try {
		const dataPalettes = stage.dataset.palettes;
		if (dataPalettes) palettes = JSON.parse(dataPalettes);
	} catch (e) {
		console.warn('Firefly: Invalid palette JSON', e);
	}

	const config = {
		total: parseInt(stage.dataset.total || 80),
		minPixel: parseFloat(stage.dataset.minPixel || 1),
		maxPixel: parseFloat(stage.dataset.maxPixel || 5),
		speed: parseFloat(stage.dataset.speed || 1),
		interactive: stage.dataset.interactive === 'true',
		palettes: palettes,
		glow: parseFloat(
			computedStyle.getPropertyValue('--firefly-glow') || 2.5
		),
		opacity: parseFloat(
			computedStyle.getPropertyValue('--firefly-opacity') || 1
		),
	};

	let fireflies = [];
	let mouse = { x: null, y: null };
	let sprites = {};
	let animationId;
	let isVisible = false;
	let lastTime = 0;

	/**
	 * Convert HEX color to RGB string (helper for RGBA transparency)
	 */
	const hexToRgb = (hex) => {
		let h = hex.replace('#', '');
		if (h.length === 3)
			h = h
				.split('')
				.map((c) => c + c)
				.join('');
		const r = parseInt(h.substring(0, 2), 16);
		const g = parseInt(h.substring(2, 4), 16);
		const b = parseInt(h.substring(4, 6), 16);
		return `${r}, ${g}, ${b}`;
	};

	/**
	 * Pre-render firefly "sprites" to avoid expensive gradient calculations in each frame.
	 * This significantly boosts performance in Firefox and mobile browsers.
	 */
	const createSprites = () => {
		sprites = {};
		config.palettes.forEach((color) => {
			const sCanvas = document.createElement('canvas');
			const sSize = 128; // Standardized sprite resolution
			sCanvas.width = sSize;
			sCanvas.height = sSize;
			const sCtx = sCanvas.getContext('2d');

			const rgb = hexToRgb(color);
			const gradient = sCtx.createRadialGradient(
				sSize / 2,
				sSize / 2,
				0,
				sSize / 2,
				sSize / 2,
				sSize / 2
			);

			gradient.addColorStop(0, '#ffffff'); // Bright nucleus
			gradient.addColorStop(0.15, color); // Color tint
			gradient.addColorStop(1, `rgba(${rgb}, 0)`); // Soft fade to transparent color

			sCtx.fillStyle = gradient;
			sCtx.fillRect(0, 0, sSize, sSize);
			sprites[color] = sCanvas;
		});
	};

	/**
	 * Handle window resize and initial firefly creation
	 */
	const resize = () => {
		const rect = stage.getBoundingClientRect();
		if (rect.width === 0 || rect.height === 0) return;

		canvas.width = rect.width;
		canvas.height = rect.height;

		// Refresh CSS-driven config
		const latestStyle = getComputedStyle(stage);
		config.glow = parseFloat(
			latestStyle.getPropertyValue('--firefly-glow') || 2.5
		);
		config.opacity = parseFloat(
			latestStyle.getPropertyValue('--firefly-opacity') || 1
		);

		createSprites();

		// Create fireflies or re-init existing ones
		if (fireflies.length !== config.total) {
			fireflies = Array.from(
				{ length: config.total },
				() => new Firefly(canvas, config)
			);
		} else {
			fireflies.forEach((f) => f.reset());
		}
	};

	/**
	 * Throttled animation loop (~60fps max)
	 */
	const loop = (time) => {
		if (!isVisible) return;

		const elapsed = time - lastTime;

		// Skip frame if running too fast (e.g. 144Hz monitors)
		if (elapsed < 16) {
			animationId = requestAnimationFrame(loop);
			return;
		}

		const dt = lastTime ? Math.min(elapsed / 16.67, 3) : 1;
		lastTime = time;

		ctx.clearRect(0, 0, canvas.width, canvas.height);

		// Update and draw each particle
		fireflies.forEach((f) => {
			f.update(mouse, dt);
			f.draw(ctx, sprites);
		});

		// Reset global state after batch draw
		ctx.globalAlpha = 1;

		animationId = requestAnimationFrame(loop);
	};

	// Event Handlers
	window.addEventListener('resize', resize);

	if (config.interactive) {
		const trackingArea = stage.parentElement || stage;

		trackingArea.addEventListener('mousemove', (e) => {
			const rect = stage.getBoundingClientRect();
			mouse.x = e.clientX - rect.left;
			mouse.y = e.clientY - rect.top;
		});

		trackingArea.addEventListener('mouseleave', () => {
			mouse.x = null;
			mouse.y = null;
		});
	}

	// Visibility observer: stop processing when slider is off-screen
	const observer = new IntersectionObserver(
		(entries) => {
			isVisible = entries[0].isIntersecting;
			if (isVisible) {
				lastTime = performance.now();
				loop(lastTime);
			} else {
				cancelAnimationFrame(animationId);
			}
		},
		{ threshold: 0.05 }
	);

	observer.observe(stage);
	resize();
};

/**
 * Scan document for firefly containers and boot them up
 */
const discoverStages = () => {
	document.querySelectorAll('.har_firefly').forEach(initFireflies);
};

// Initial boot
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', discoverStages);
} else {
	discoverStages();
}

/**
 * Dynamic content support (Gutenberg blocks, AJAX loading, etc.)
 */
new MutationObserver((mutations) => {
	mutations.forEach((mutation) => {
		mutation.addedNodes.forEach((node) => {
			if (node.nodeType !== 1) return;
			if (node.classList.contains('har_firefly')) initFireflies(node);
			node.querySelectorAll('.har_firefly').forEach(initFireflies);
		});
	});
}).observe(document.body, { childList: true, subtree: true });
